import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaSearch,
  FaDownload,
  FaEye,
  FaArrowLeft,
  FaGraduationCap,
  FaFilePdf,
  FaCalendarAlt,
  FaChevronLeft,
  FaCode,
  FaMicrochip,
  FaCogs,
  FaBuilding,
  FaPlane,
  FaFlask,
  FaBrain,
  FaRobot,
  FaShieldAlt,
  FaNetworkWired,
  FaChartLine,
  FaDatabase,
  FaIndustry,
} from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import Loader from "../components/common/Loader";
import { useFooter } from "../context/FooterContext";
import PDFViewer from "../components/pyqs/PDFViewer";

// ========== PRODUCTION-READY DYNAMIC FUNCTIONS (NO HARDCODING) ==========

// Extract branch from subject code (BCS401 -> CS, BEC401 -> EC, BME301 -> ME, etc.)
const extractBranchFromSubjectCode = (subjectCode) => {
  if (!subjectCode || typeof subjectCode !== "string") return null;

  // Pattern: First letter B (Bachelor), then 2 letters for branch, then 3 digits
  // BCS401 -> CS, BEC401 -> EC, BME301 -> ME, BIS501 -> IS, etc.
  const match = subjectCode.match(/^B([A-Z]{2})\d{3}/);
  if (match) {
    return match[1]; // Returns CS, EC, ME, IS, etc.
  }

  // Alternative pattern: Just the middle 2 letters
  const altMatch = subjectCode.match(/[A-Z]{2}\d{3}/);
  if (altMatch) {
    return altMatch[0].substring(0, 2);
  }

  return null;
};

// Extract semester from subject code (BCS401 -> 4, BEC301 -> 3, BME701 -> 7)
const extractSemesterFromSubjectCode = (subjectCode) => {
  if (!subjectCode || typeof subjectCode !== "string") return null;

  // Pattern: Find 3-digit number, take first digit
  const match = subjectCode.match(/(\d{3})/);
  if (match) {
    const threeDigit = match[1];
    const semester = parseInt(threeDigit.charAt(0));
    if (semester >= 1 && semester <= 8) {
      return semester;
    }
  }

  return null;
};

// Get display branch name (CS -> CSE, EC -> ECE, etc.) - DYNAMIC mapping
const getDisplayBranchName = (branchCode) => {
  if (!branchCode) return branchCode;

  // Dynamic mapping based on common patterns
  const displayMap = {
    CS: "CSE",
    IS: "ISE",
    EC: "ECE",
    UE: "ECE",
    EE: "EEE",
    ME: "MECH",
    CV: "CIVIL",
    AE: "AE",
    BT: "BT",
    AD: "ADS",
    AI: "AIML",
    CO: "CSIoT",
    CY: "CSBS",
    CB: "CSBS",
    RI: "RAI",
  };

  return displayMap[branchCode] || branchCode;
};

// Get full branch name from branch code
const getFullBranchName = (branchCode) => {
  if (!branchCode) return branchCode;

  const branchMap = {
    CS: "Computer Science and Engineering",
    IS: "Information Science and Engineering",
    EC: "Electronics and Communication Engineering",
    UE: "Electronics and Computer Engineering",
    EE: "Electrical and Electronics Engineering",
    ME: "Mechanical Engineering",
    CV: "Civil Engineering",
    AE: "Aeronautical Engineering",
    BT: "Biotechnology",
    AD: "Artificial Intelligence & Data Science",
    AI: "Artificial Intelligence and Machine Learning",
    CO: "Computer Science and Engineering (IoT)",
    CY: "CSE (Cyber Security)",
    CB: "Computer Science and Business System",
    RI: "Robotics & Artificial Intelligence",
  };

  return branchMap[branchCode] || branchCode;
};

// Get branch icon based on branch code
const getBranchIcon = (branchCode) => {
  const code = branchCode?.toUpperCase() || "";

  if (code === "CS" || code === "CO" || code === "CB")
    return <FaCode size={24} />;
  if (code === "IS") return <FaDatabase size={24} />;
  if (code === "EC" || code === "UE") return <FaMicrochip size={24} />;
  if (code === "EE" || code === "ME") return <FaCogs size={24} />;
  if (code === "CV") return <FaBuilding size={24} />;
  if (code === "AE") return <FaPlane size={24} />;
  if (code === "BT") return <FaFlask size={24} />;
  if (code === "AD" || code === "AI" || code === "RI")
    return <FaBrain size={24} />;
  if (code === "CY") return <FaShieldAlt size={24} />;

  return <FaGraduationCap size={24} />;
};

// Get branch color based on branch code
const getBranchColor = (branchCode) => {
  const code = branchCode?.toUpperCase() || "";

  if (code === "CS" || code === "CO" || code === "CB") return "#3b82f6";
  if (code === "IS") return "#8b5cf6";
  if (code === "EC" || code === "UE") return "#10b981";
  if (code === "EE" || code === "ME") return "#f59e0b";
  if (code === "CV") return "#84cc16";
  if (code === "AE") return "#06b6d4";
  if (code === "BT") return "#ec4899";
  if (code === "AD" || code === "AI" || code === "RI") return "#a855f7";
  if (code === "CY") return "#ef4444";

  return "#8b5cf6";
};

const PYQs = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { setShowFooter } = useFooter();
  const [allPyqs, setAllPyqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedSemester, setSelectedSemester] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [branchSearchTerm, setBranchSearchTerm] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [viewingPdf, setViewingPdf] = useState(null);

  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  useEffect(() => {
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    fetchAllPYQs();
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => setIsMobile(window.innerWidth <= 768);

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const fetchAllPYQs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/pyqs?limit=500`);
      setAllPyqs(response.data.pyqs);
    } catch (error) {
      toast.error("Failed to fetch PYQs");
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Download handler for direct PDF response
  const handleDownload = async (pyqId) => {
    if (!pyqId) {
      toast.error("Invalid paper ID");
      return;
    }
    try {
      const response = await axios.get(`/api/pyqs/download/${pyqId}`, {
        responseType: "blob",
      });

      // Create a blob from the PDF data
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `pyq-${pyqId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Download started!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error(error.response?.data?.message || "Failed to download");
    }
  };

  const handleReset = () => {
    setSelectedBranch("");
    setSelectedSemester("");
    setSearchTerm("");
    setBranchSearchTerm("");
  };

  // Process PYQs to extract branch and correct semester from subject code
  const processedPyqs = allPyqs.map((pyq) => {
    const extractedBranch = extractBranchFromSubjectCode(pyq.subjectCode);
    const extractedSemester = extractSemesterFromSubjectCode(pyq.subjectCode);

    return {
      ...pyq,
      extractedBranch: extractedBranch || pyq.branch,
      correctSemester: extractedSemester || pyq.semester,
    };
  });

  // Get unique branches from processed data
  const getAvailableBranches = () => {
    const branchesMap = new Map();
    processedPyqs.forEach((pyq) => {
      const branchCode = pyq.extractedBranch;
      if (branchCode && !branchesMap.has(branchCode)) {
        branchesMap.set(branchCode, {
          code: branchCode,
          displayName: getDisplayBranchName(branchCode),
          fullName: getFullBranchName(branchCode),
          paperCount: 0,
        });
      }
      if (branchCode) {
        const branch = branchesMap.get(branchCode);
        if (branch) branch.paperCount++;
      }
    });

    let available = Array.from(branchesMap.values());

    if (branchSearchTerm) {
      const term = branchSearchTerm.toLowerCase();
      available = available.filter(
        (b) =>
          b.code.toLowerCase().includes(term) ||
          b.displayName.toLowerCase().includes(term) ||
          b.fullName.toLowerCase().includes(term),
      );
    }

    return available.sort((a, b) => a.code.localeCompare(b.code));
  };

  // Get filtered PYQs
  const getFilteredPyqs = () => {
    let filtered = processedPyqs;

    if (selectedBranch) {
      filtered = filtered.filter((p) => p.extractedBranch === selectedBranch);
    }
    if (selectedSemester) {
      filtered = filtered.filter(
        (p) => p.correctSemester === parseInt(selectedSemester),
      );
    }
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.title.toLowerCase().includes(searchLower) ||
          p.subjectCode.toLowerCase().includes(searchLower) ||
          p.subject.toLowerCase().includes(searchLower) ||
          p.examYear.includes(searchTerm),
      );
    }
    return filtered;
  };

  // Group filtered PYQs by Subject Code
  const getGroupedPyqs = () => {
    const filtered = getFilteredPyqs();
    const grouped = {};
    filtered.forEach((pyq) => {
      if (!grouped[pyq.subjectCode]) {
        grouped[pyq.subjectCode] = {
          subjectCode: pyq.subjectCode,
          subjectName: pyq.subject,
          semester: pyq.correctSemester,
          branch: pyq.extractedBranch,
          papers: [],
        };
      }
      grouped[pyq.subjectCode].papers.push(pyq);
    });

    Object.keys(grouped).forEach((code) => {
      grouped[code].papers.sort(
        (a, b) => parseInt(b.examYear) - parseInt(a.examYear),
      );
    });

    return grouped;
  };

  // Get available semesters for selected branch
  const getAvailableSemesters = () => {
    if (!selectedBranch) return [];
    const semestersSet = new Set();
    processedPyqs
      .filter((p) => p.extractedBranch === selectedBranch)
      .forEach((p) => semestersSet.add(p.correctSemester));
    return Array.from(semestersSet).sort((a, b) => a - b);
  };

  const availableBranches = getAvailableBranches();
  const groupedPyqs = getGroupedPyqs();
  const totalSubjects = Object.keys(groupedPyqs).length;
  const totalPapers = getFilteredPyqs().length;
  const availableSemesters = getAvailableSemesters();

  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "white";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  if (loading) return <Loader />;

  // Branch Selection Screen
  if (!selectedBranch) {
    return (
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: isMobile ? "1rem" : "2rem",
          background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
          minHeight: "calc(100vh - 200px)",
        }}
      >
        <button
          onClick={() => window.history.back()}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            color: "#8b5cf6",
            background: "none",
            border: "none",
            cursor: "pointer",
            marginBottom: "1.5rem",
            fontSize: "0.9rem",
          }}
        >
          <FaArrowLeft /> Back
        </button>

        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div
            style={{
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              width: "70px",
              height: "70px",
              borderRadius: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 1rem auto",
              boxShadow: "0 10px 25px -5px rgba(139,92,246,0.3)",
            }}
          >
            <FaFilePdf size={32} color="white" />
          </div>
          <h1
            style={{
              fontSize: isMobile ? "1.8rem" : "2.2rem",
              fontWeight: "bold",
              color: getTextColor(),
              marginBottom: "0.5rem",
            }}
          >
            Previous Year <span style={{ color: "#8b5cf6" }}>Papers</span>
          </h1>
          <p style={{ color: getTextSecondary() }}>
            Select your branch to view previous year question papers
          </p>
        </div>

        {/* Search Bar for Branches */}
        <div
          style={{
            background: getCardBackground(),
            borderRadius: "16px",
            padding: "0.8rem",
            marginBottom: "1.5rem",
            border: `1px solid ${getBorderColor()}`,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              background: getInputBackground(),
              padding: "0.3rem",
              borderRadius: "50px",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <input
              type="text"
              placeholder="Search by branch name or code..."
              value={branchSearchTerm}
              onChange={(e) => setBranchSearchTerm(e.target.value)}
              style={{
                flex: 1,
                padding: "0.7rem 1rem",
                border: "none",
                outline: "none",
                fontSize: "0.85rem",
                background: "transparent",
                color: getTextColor(),
              }}
            />
            <button
              style={{
                padding: "0.7rem 1.2rem",
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                color: "white",
                border: "none",
                borderRadius: "50px",
                cursor: "pointer",
                fontSize: "0.8rem",
              }}
            >
              <FaSearch /> Search
            </button>
          </div>
        </div>

        {/* Branches Grid */}
        {availableBranches.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "3rem",
              background: getCardBackground(),
              borderRadius: "20px",
              border: `1px solid ${getBorderColor()}`,
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            }}
          >
            <FaFilePdf size={48} color={getTextSecondary()} />
            <h3 style={{ color: getTextColor(), marginTop: "1rem" }}>
              No branches found
            </h3>
            <p style={{ color: getTextSecondary() }}>
              No previous year papers have been uploaded yet
            </p>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(auto-fill, minmax(340px, 1fr))",
              gap: "1rem",
            }}
          >
            {availableBranches.map((branch) => {
              const branchColor = getBranchColor(branch.code);
              const branchIcon = getBranchIcon(branch.code);
              return (
                <motion.div
                  key={branch.code}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -4, scale: 1.02 }}
                  onClick={() => setSelectedBranch(branch.code)}
                  style={{
                    background: getCardBackground(),
                    borderRadius: "16px",
                    padding: "1.2rem",
                    border: `1px solid ${getBorderColor()}`,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    cursor: "pointer",
                    transition: "all 0.3s",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.8rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <div
                      style={{
                        width: "50px",
                        height: "50px",
                        background: `linear-gradient(135deg, ${branchColor}, ${branchColor}80)`,
                        borderRadius: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <div style={{ color: "white" }}>{branchIcon}</div>
                    </div>
                    <div>
                      <h3
                        style={{
                          color: getTextColor(),
                          margin: 0,
                          fontSize: "1rem",
                          fontWeight: 600,
                        }}
                      >
                        {branch.displayName}
                      </h3>
                      <p
                        style={{
                          color: getTextSecondary(),
                          margin: "0.2rem 0 0",
                          fontSize: "0.7rem",
                        }}
                      >
                        {branch.fullName}
                      </p>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "0.5rem",
                    }}
                  >
                    <span
                      style={{
                        color: "#8b5cf6",
                        fontSize: "0.75rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.3rem",
                      }}
                    >
                      <FaFilePdf size={12} /> {branch.paperCount} papers
                    </span>
                    <span
                      style={{ color: getTextSecondary(), fontSize: "0.7rem" }}
                    >
                      Click →
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Semester Selection Screen
  if (selectedBranch && !selectedSemester) {
    const branchColor = getBranchColor(selectedBranch);
    const branchIcon = getBranchIcon(selectedBranch);
    const displayBranchName = getDisplayBranchName(selectedBranch);
    const fullBranchName = getFullBranchName(selectedBranch);

    return (
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: isMobile ? "1rem" : "2rem",
          background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
          minHeight: "calc(100vh - 200px)",
        }}
      >
        <button
          onClick={() => setSelectedBranch("")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            color: "#8b5cf6",
            background: "none",
            border: "none",
            cursor: "pointer",
            marginBottom: "1.5rem",
            fontSize: "0.9rem",
          }}
        >
          <FaChevronLeft /> Back to Branches
        </button>

        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div
            style={{
              background: `linear-gradient(135deg, ${branchColor}, ${branchColor}80)`,
              width: "80px",
              height: "80px",
              borderRadius: "28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 1rem auto",
            }}
          >
            <div style={{ color: "white", fontSize: "2rem" }}>{branchIcon}</div>
          </div>
          <h1
            style={{
              fontSize: isMobile ? "1.8rem" : "2rem",
              fontWeight: "bold",
              color: getTextColor(),
              marginBottom: "0.3rem",
            }}
          >
            {displayBranchName}
          </h1>
          <p style={{ color: getTextSecondary(), fontSize: "0.85rem" }}>
            {fullBranchName}
          </p>
          <p style={{ color: getTextSecondary(), marginTop: "0.5rem" }}>
            Select semester to view question papers
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
            gap: "1rem",
          }}
        >
          {availableSemesters.map((sem) => {
            const paperCount = processedPyqs.filter(
              (p) =>
                p.extractedBranch === selectedBranch &&
                p.correctSemester === sem,
            ).length;
            return (
              <motion.div
                key={sem}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -4, scale: 1.02 }}
                onClick={() => setSelectedSemester(sem)}
                style={{
                  background: getCardBackground(),
                  borderRadius: "16px",
                  padding: "1.5rem",
                  border: `1px solid ${getBorderColor()}`,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  cursor: "pointer",
                  textAlign: "center",
                  transition: "all 0.3s",
                }}
              >
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    background: `linear-gradient(135deg, ${branchColor}, ${branchColor}80)`,
                    borderRadius: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 0.8rem auto",
                  }}
                >
                  <FaGraduationCap size={28} color="white" />
                </div>
                <h2
                  style={{
                    color: getTextColor(),
                    margin: 0,
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                  }}
                >
                  {sem}
                </h2>
                <p
                  style={{
                    color: getTextSecondary(),
                    margin: "0.3rem 0 0",
                    fontSize: "0.7rem",
                  }}
                >
                  Semester {sem}
                </p>
                <p
                  style={{
                    color: "#8b5cf6",
                    margin: "0.5rem 0 0",
                    fontSize: "0.65rem",
                  }}
                >
                  {paperCount} paper{paperCount !== 1 ? "s" : ""}
                </p>
              </motion.div>
            );
          })}
        </div>

        {availableSemesters.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "3rem",
              background: getCardBackground(),
              borderRadius: "20px",
              border: `1px solid ${getBorderColor()}`,
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            }}
          >
            <FaFilePdf size={48} color={getTextSecondary()} />
            <h3 style={{ color: getTextColor(), marginTop: "1rem" }}>
              No semesters found
            </h3>
            <p style={{ color: getTextSecondary() }}>
              No papers available for this branch yet
            </p>
            <button
              onClick={() => setSelectedBranch("")}
              style={{
                marginTop: "1rem",
                padding: "0.5rem 1rem",
                background: "#8b5cf6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              Select Different Branch
            </button>
          </div>
        )}
      </div>
    );
  }

  // Papers Display Screen
  const branchColor = getBranchColor(selectedBranch);
  const branchIcon = getBranchIcon(selectedBranch);
  const displayBranchName = getDisplayBranchName(selectedBranch);
  const fullBranchName = getFullBranchName(selectedBranch);

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: isMobile ? "1rem" : "2rem",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        minHeight: "calc(100vh - 200px)",
      }}
    >
      <button
        onClick={() => setSelectedSemester("")}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.5rem",
          color: "#8b5cf6",
          background: "none",
          border: "none",
          cursor: "pointer",
          marginBottom: "1rem",
          fontSize: "0.9rem",
        }}
      >
        <FaChevronLeft /> Back to Semesters
      </button>

      <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
        <div
          style={{
            background: `linear-gradient(135deg, ${branchColor}, ${branchColor}80)`,
            width: "60px",
            height: "60px",
            borderRadius: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 0.8rem auto",
          }}
        >
          <div style={{ color: "white", fontSize: "1.5rem" }}>{branchIcon}</div>
        </div>
        <h1
          style={{
            fontSize: isMobile ? "1.5rem" : "1.8rem",
            fontWeight: "bold",
            color: getTextColor(),
            marginBottom: "0.2rem",
          }}
        >
          {displayBranchName} - Semester {selectedSemester}
        </h1>
        <p style={{ color: getTextSecondary(), fontSize: "0.8rem" }}>
          {fullBranchName}
        </p>
        <p style={{ color: getTextSecondary(), marginTop: "0.3rem" }}>
          Previous year question papers
        </p>
      </div>

      {/* Search Bar */}
      <div
        style={{
          background: getCardBackground(),
          borderRadius: "16px",
          padding: "0.8rem",
          marginBottom: "1.5rem",
          border: `1px solid ${getBorderColor()}`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            background: getInputBackground(),
            padding: "0.3rem",
            borderRadius: "50px",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <input
            type="text"
            placeholder="Search by subject code, name, or title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              padding: "0.7rem 1rem",
              border: "none",
              outline: "none",
              fontSize: "0.85rem",
              background: "transparent",
              color: getTextColor(),
            }}
          />
          <button
            style={{
              padding: "0.7rem 1.2rem",
              background: `linear-gradient(135deg, ${branchColor}, ${branchColor})`,
              color: "white",
              border: "none",
              borderRadius: "50px",
              cursor: "pointer",
              fontSize: "0.8rem",
            }}
          >
            <FaSearch /> Search
          </button>
        </div>
      </div>

      {/* Results Stats */}
      <div style={{ marginBottom: "1rem", padding: "0 0.5rem" }}>
        <p style={{ color: getTextSecondary(), fontSize: "0.8rem" }}>
          Found <strong style={{ color: "#8b5cf6" }}>{totalSubjects}</strong>{" "}
          subjects with{" "}
          <strong style={{ color: "#8b5cf6" }}>{totalPapers}</strong> papers
          {searchTerm && <span> • Search: "{searchTerm}"</span>}
        </p>
      </div>

      {/* Papers Display */}
      {totalPapers === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            background: getCardBackground(),
            borderRadius: "20px",
            border: `1px solid ${getBorderColor()}`,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          <FaFilePdf size={48} color={getTextSecondary()} />
          <h3 style={{ color: getTextColor(), marginTop: "1rem" }}>
            No papers found
          </h3>
          <p style={{ color: getTextSecondary() }}>Try adjusting your search</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {Object.values(groupedPyqs).map((subject) => {
            const latestPaper = subject.papers[0];
            const hasMultiplePapers = subject.papers.length > 1;
            return (
              <div
                key={subject.subjectCode}
                style={{
                  background: getCardBackground(),
                  borderRadius: "16px",
                  border: `1px solid ${getBorderColor()}`,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "1rem 1.2rem",
                    background: isDarkMode ? "#25253a" : "#f8f9fa",
                    borderBottom: `1px solid ${getBorderColor()}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "0.5rem",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.6rem",
                          flexWrap: "wrap",
                          marginBottom: "0.3rem",
                        }}
                      >
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontWeight: "bold",
                            color: "#8b5cf6",
                            fontSize: "1rem",
                          }}
                        >
                          {subject.subjectCode}
                        </span>
                        <span
                          style={{
                            background: "#8b5cf6",
                            color: "white",
                            padding: "0.15rem 0.5rem",
                            borderRadius: "20px",
                            fontSize: "0.65rem",
                          }}
                        >
                          {subject.papers.length} paper
                          {subject.papers.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <p
                        style={{
                          color: getTextColor(),
                          fontSize: "0.85rem",
                          margin: 0,
                        }}
                      >
                        {subject.subjectName}
                      </p>
                    </div>
                    {latestPaper.examYear && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.3rem",
                          color: getTextSecondary(),
                          fontSize: "0.7rem",
                        }}
                      >
                        <FaCalendarAlt size={11} /> Latest:{" "}
                        {latestPaper.examYear}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ padding: "0.5rem 0" }}>
                  {subject.papers.map((paper, idx) => (
                    <div
                      key={paper._id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "0.8rem",
                        padding: "0.8rem 1.2rem",
                        borderTop:
                          idx === 0 ? "none" : `1px solid ${getBorderColor()}`,
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = isDarkMode
                          ? "rgba(139,92,246,0.08)"
                          : "rgba(139,92,246,0.04)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.6rem",
                            flexWrap: "wrap",
                            marginBottom: "0.2rem",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.3rem",
                            }}
                          >
                            <FaCalendarAlt
                              size={11}
                              color={getTextSecondary()}
                            />
                            <span
                              style={{
                                color: getTextColor(),
                                fontSize: "0.8rem",
                                fontWeight: idx === 0 ? "500" : "normal",
                              }}
                            >
                              {paper.examYear}
                            </span>
                          </div>
                          <span
                            style={{
                              background:
                                paper.examType === "External"
                                  ? "#10b981"
                                  : paper.examType === "Internal"
                                    ? "#f59e0b"
                                    : "#8b5cf6",
                              color: "white",
                              padding: "0.15rem 0.5rem",
                              borderRadius: "20px",
                              fontSize: "0.65rem",
                            }}
                          >
                            {paper.examType}
                          </span>
                          {idx === 0 && hasMultiplePapers && (
                            <span
                              style={{
                                background: "#8b5cf6",
                                color: "white",
                                padding: "0.15rem 0.4rem",
                                borderRadius: "20px",
                                fontSize: "0.6rem",
                              }}
                            >
                              Latest
                            </span>
                          )}
                        </div>
                        <p
                          style={{
                            color: getTextSecondary(),
                            fontSize: "0.75rem",
                            margin: 0,
                          }}
                        >
                          {paper.title.length > 70
                            ? paper.title.substring(0, 70) + "..."
                            : paper.title}
                        </p>
                      </div>

                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button
                          onClick={() => setViewingPdf(paper)}
                          style={{
                            padding: "0.3rem 0.9rem",
                            background: "#8b5cf6",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.3rem",
                            fontSize: "0.7rem",
                          }}
                        >
                          <FaEye size={11} /> View
                        </button>
                        {/* FIXED DOWNLOAD BUTTON */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(paper._id || paper.id);
                          }}
                          style={{
                            padding: "0.3rem 0.9rem",
                            background: "#10b981",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.3rem",
                            fontSize: "0.7rem",
                          }}
                        >
                          <FaDownload size={11} /> Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {viewingPdf && (
        <PDFViewer
          pyqId={viewingPdf._id}
          title={viewingPdf.title}
          onClose={() => setViewingPdf(null)}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
};

export default PYQs;
